import { GameManager } from "@/shared/services/gameManager";
import { PlayerDetector } from "@/shared/services/playerDetector";
import type {
  Game,
  GameUrls,
  PlayerType,
  RufflePlayerElement,
  UrlGame,
} from "@/shared/types/flash";
import { useEffect, useRef, useState } from "react";
import styles from "./FlashPlayer.module.css";
import { FlashPlayerContent } from "./FlashPlayerContent";
import { FlashPlayerHeader } from "./FlashPlayerHeader";

interface FlashPlayerProps {
  game: Game;
  onExit: () => void;
}

type PlayerMode = PlayerType | "iframe";

export function FlashPlayer({ game, onExit }: FlashPlayerProps) {
  const [mode, setMode] = useState<PlayerMode | null>(null);
  const [gameUrls, setGameUrls] = useState<GameUrls | UrlGame | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availablePlayers, setAvailablePlayers] = useState({
    ruffle: false,
    flash: false,
  });

  const ruffleContainerRef = useRef<HTMLDivElement>(null);
  const flashContainerRef = useRef<HTMLDivElement>(null);
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const rufflePlayerRef = useRef<HTMLElement | null>(null);
  const objectRef = useRef<HTMLElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Проверяем доступные плееры при монтировании
  useEffect(() => {
    const checkPlayers = async () => {
      // Сначала проверяем тип игры
      try {
        const gameInfo = await GameManager.getGameInfo(game.appId);
        const gameType = gameInfo.type || (gameInfo.url ? "url" : "local");

        // Если это URL игра, сразу переключаемся на iframe режим
        if (gameType === "url" && gameInfo.url) {
          console.log("[FlashPlayer] URL game detected, using iframe mode");
          setMode("iframe");
          setIsLoading(false);
          return;
        }
      } catch (err) {
        console.warn("[FlashPlayer] Failed to get game info:", err);
      }

      // Для локальных игр проверяем доступные плееры
      const availability = await PlayerDetector.checkAll();
      setAvailablePlayers({
        ruffle: availability.ruffle,
        flash: availability.flash,
      });

      // Устанавливаем рекомендуемый плеер
      setMode(availability.recommended);

      if (!availability.ruffle && !availability.flash) {
        setError("Не найдено ни одного доступного плеера для Flash");
        setIsLoading(false);
      }
    };

    checkPlayers();
  }, [game.appId]);

  // Загружаем игру
  useEffect(() => {
    if (!mode) return;

    const loadGame = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Для iframe режима не нужно скачивать файлы
        if (mode === "iframe") {
          const gameInfo = await GameManager.getGameInfo(game.appId);
          if (gameInfo.type === "url" && gameInfo.url) {
            setGameUrls({ type: "url", url: gameInfo.url });
            setIsLoading(false);
            return;
          }
        }

        // Очистка старых временных файлов (только для локальных игр)
        await GameManager.clearOldTempFiles();

        // Загружаем SWF файл и получаем URLs
        const urls = await GameManager.downloadGame(game.appId);
        setGameUrls(urls);

        if ("type" in urls && urls.type === "url") {
          console.log(`[FlashPlayer] URL game loaded:`, urls.url);
        } else {
          const gameUrls = urls as GameUrls;
          console.log(`[FlashPlayer] Game loaded:`, {
            dataUrlSize: gameUrls.dataUrl.length,
            fileUrl: gameUrls.fileUrl,
            localPath: gameUrls.localPath,
          });
        }
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "Неизвестная ошибка";
        setError(errorMsg);
        console.error("Ошибка загрузки игры:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadGame();
  }, [game.appId, mode]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      // Cleanup Ruffle player
      if (rufflePlayerRef.current) {
        try {
          if ("remove" in rufflePlayerRef.current) {
            (rufflePlayerRef.current as RufflePlayerElement).remove();
          }
          rufflePlayerRef.current = null;
        } catch (e) {
          console.warn("Failed to cleanup Ruffle player:", e);
        }
      }

      // Cleanup Flash object
      if (objectRef.current && objectRef.current.parentNode) {
        objectRef.current.parentNode.removeChild(objectRef.current);
        objectRef.current = null;
      }

      // Cleanup iframe
      if (iframeRef.current && iframeRef.current.parentNode) {
        iframeRef.current.parentNode.removeChild(iframeRef.current);
        iframeRef.current = null;
      }
    };
  }, []);

  // Ruffle отключен из-за несовместимости с Electron 9.4.4
  useEffect(() => {
    if (mode !== "ruffle" || !gameUrls) return;

    const container = ruffleContainerRef.current;
    if (!container) return;

    // Показываем сообщение что Ruffle недоступен
    container.innerHTML = `
      <div class="${styles.fallbackContent}">
        <p>❌ Ruffle недоступен</p>
        <p>Electron 9.4.4 не поддерживает современный JavaScript синтаксис</p>
        <p>Используйте Flash плеер</p>
      </div>
    `;

    // Автоматическое переключение на Flash
    if (availablePlayers.flash) {
      console.log("[FlashPlayer] Auto-switching to Flash...");
      setTimeout(() => setMode("flash"), 1000);
    }

    return () => {
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [mode, gameUrls, availablePlayers.flash]);

  // Инициализация Flash плеера
  useEffect(() => {
    if (mode !== "flash" || !gameUrls || !flashContainerRef.current) return;

    // Проверяем, что это не URL игра
    if ("type" in gameUrls && gameUrls.type === "url") {
      return;
    }

    // Очищаем предыдущий embed/object
    flashContainerRef.current.innerHTML = "";

    // Создаем embed элемент для Flash (более надежный способ)
    const embed = document.createElement("embed");
    embed.type = "application/x-shockwave-flash";
    embed.src = (gameUrls as GameUrls).fileUrl; // Используем swflocal:// протокол
    embed.width = "100%";
    embed.height = "100%";
    embed.style.border = "none";
    embed.setAttribute("allowScriptAccess", "always");
    embed.setAttribute("quality", "high");
    embed.setAttribute("bgcolor", "#000000");
    embed.setAttribute("wmode", "direct");
    embed.setAttribute("allowFullScreen", "true");

    flashContainerRef.current.appendChild(embed);
    objectRef.current = embed;

    console.log(
      "[FlashPlayer] Flash player initialized with:",
      (gameUrls as GameUrls).fileUrl
    );

    return () => {
      if (objectRef.current && objectRef.current.parentNode) {
        objectRef.current.parentNode.removeChild(objectRef.current);
        objectRef.current = null;
      }
    };
  }, [mode, gameUrls]);

  // Инициализация iframe для URL игр
  useEffect(() => {
    if (mode !== "iframe" || !gameUrls || !iframeContainerRef.current) return;

    // Проверяем, что это URL игра
    if (!("type" in gameUrls) || gameUrls.type !== "url") {
      return;
    }

    // Очищаем предыдущий iframe
    iframeContainerRef.current.innerHTML = "";

    // Создаем iframe элемент
    const iframe = document.createElement("iframe");
    iframe.src = gameUrls.url;
    iframe.width = "100%";
    iframe.height = "100%";
    iframe.style.border = "none";
    iframe.setAttribute("allowFullScreen", "true");
    // Не используем sandbox, так как он блокирует загрузку плагинов (Flash)
    // Безопасность контролируется на уровне Electron (webSecurity, plugins и т.д.)

    // Обработка ошибок загрузки
    iframe.onerror = () => {
      console.error("[FlashPlayer] Failed to load iframe");
      setError(
        "Не удалось загрузить страницу. Возможно, сайт блокирует встраивание в iframe."
      );
    };

    // Обработка успешной загрузки
    iframe.onload = () => {
      console.log("[FlashPlayer] Iframe loaded successfully:", gameUrls.url);
    };

    iframeContainerRef.current.appendChild(iframe);
    iframeRef.current = iframe;

    console.log("[FlashPlayer] Iframe initialized with:", gameUrls.url);

    return () => {
      if (iframeRef.current && iframeRef.current.parentNode) {
        iframeRef.current.parentNode.removeChild(iframeRef.current);
        iframeRef.current = null;
      }
    };
  }, [mode, gameUrls]);

  const handleRetry = () => {
    window.location.reload();
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <FlashPlayerHeader
          gameTitle={game.title}
          onExit={onExit}
          mode={mode}
          availablePlayers={availablePlayers}
          onModeChange={setMode}
        />
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Загрузка игры...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <FlashPlayerHeader
          gameTitle={game.title}
          onExit={onExit}
          mode={mode}
          availablePlayers={availablePlayers}
          onModeChange={setMode}
        />
        <div className={styles.errorContainer}>
          <p>❌ Ошибка загрузки игры: {error}</p>
          <button className={styles.retryButton} onClick={handleRetry}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <FlashPlayerHeader
        gameTitle={game.title}
        onExit={onExit}
        mode={mode}
        availablePlayers={availablePlayers}
        onModeChange={setMode}
      />
      <FlashPlayerContent
        mode={mode}
        gameUrls={gameUrls}
        ruffleContainerRef={ruffleContainerRef}
        flashContainerRef={flashContainerRef}
        iframeContainerRef={iframeContainerRef}
      />
      {/* <FlashPlayerInfo mode={mode} availablePlayers={availablePlayers} /> */}
    </div>
  );
}
